import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Anchor, Send, Sparkles, Share2, LayoutDashboard, CheckCircle2, Ship, Building2, Upload, Undo2 } from "lucide-react";
import { formatDateInput } from "@/lib/formatDate";
import { PhoneField } from "@/components/PhoneField";
import { isValidPhoneNumber } from "react-phone-number-input";
import { GetNoticedFaster } from "@/components/GetNoticedFaster";
import { getRef } from "@/lib/referral";

const uid = () => Math.random().toString(36).slice(2);

// Local fallback so the review step always shows an editable summary, even if the AI call is slow/unavailable.
const localSummary = (role, d = {}) => {
  const coc = Array.isArray(d.coc) ? d.coc.join(", ") : d.coc;
  if (role === "seeker")
    return `${d.rank || "Seafarer"} with ${d.sea_time || ""} of sea time on ${d.vessel_type || ""} vessels. COC: ${coc || "—"}. US Visa: ${d.us_visa || "—"}. Available from ${d.availability || "—"}.`.replace(/\s+/g, " ").trim();
  return `Seeking a ${d.rank || "crew"} for ${d.vessel_type || ""} vessels with ${d.sea_time || ""} experience. Preferred COC: ${coc || "—"}. US Visa: ${d.us_visa || "—"}. Joining: ${d.availability || "—"}.`.replace(/\s+/g, " ").trim();
};

const buildSeekerSteps = (cfg) => [
  { key: "rank", bot: "What is your professional rank?", type: "dropdown", options: cfg.ranks },
  { key: "mobile", bot: "Pick your country and enter your mobile number for OTP verification.", type: "mobile", placeholder: "Mobile number" },
  { key: "otp", bot: "Enter the 6-digit OTP you received.", type: "otp" },
  { key: "cv_upload", bot: "Great! Please upload your CV / biodata now — PDF, Word or a photo (even handwritten). This saves your profile right away, even if you stop midway. You can also skip and upload it later.", type: "upload", optional: true },
  { key: "vessel_type", bot: "Which vessel type do you prefer to work on?", type: "dropdown", options: cfg.vessel_types },
  { key: "sea_time", bot: "How much sea time do you have (in months)?", type: "quick", options: cfg.sea_time },
  { key: "coc", bot: "Which country/authority issued your Certificate / License / COC?", type: "dropdown", options: cfg.coc_seeker },
  { key: "us_visa", bot: "What is your US Visa status?", type: "quick", options: cfg.us_visa_seeker },
  { key: "other_visa", bot: "Any other visa details? e.g. Schengen, UK. (Optional)", type: "text", optional: true, placeholder: "Type details or press Skip" },
  { key: "consent", bot: "We'll use your rank, experience, certificates, availability and contact details <b>only</b> to match you with shipping employers and notify you of jobs. You can withdraw consent and request deletion anytime (see Privacy Policy). Do you consent?", type: "quick", options: ["Yes", "No"] },
  { key: "name", bot: "Great! What is your full name?", type: "text", placeholder: "Full name" },
  { key: "email", bot: "Your email ID for job notifications?", type: "text", placeholder: "you@example.com" },
  { key: "availability", bot: "When are you available to join?", type: "quick", options: ["Immediate", "Custom date"] },
  { key: "review", bot: "Here is your AI-generated profile. Review, edit if needed, and submit.", type: "review" },
];

const buildProviderSteps = (cfg) => [
  { key: "rank", bot: "Which rank are you hiring for?", type: "dropdown", options: cfg.ranks },
  { key: "vessel_type", bot: "Which vessel type is this for?", type: "dropdown", options: cfg.vessel_types },
  { key: "sea_time", bot: "Minimum sea time experience required?", type: "quick", options: cfg.sea_time },
  { key: "sea_time_priority", bot: "Is this experience duration Mandatory or Preferred?", type: "quick", options: ["Mandatory", "Preferred"] },
  { key: "coc", bot: "Which COC issuing countries/authorities are acceptable? Select all that apply — crew holding any of the following will be matched.", type: "multiselect", options: cfg.coc_provider },
  { key: "us_visa", bot: "US Visa requirement for this role?", type: "quick", options: cfg.us_visa_provider },
  { key: "mobile", bot: "Pick your country and enter your mobile number for OTP verification.", type: "mobile", placeholder: "Mobile number" },
  { key: "is_rpsl", bot: "Are you an RPSL-valid company (licensed by DG Shipping)?", type: "dropdown", options: ["Yes", "No"] },
  { key: "rpsl_number", bot: "Enter your company's RPSL (Recruitment &amp; Placement Services Licence) number. We'll verify it against the DG Shipping approved list and fetch your company name.", type: "text", placeholder: "e.g. RPSL-MUM-162077" },
  { key: "otp", bot: "Enter the 6-digit OTP you received.", type: "otp" },
  { key: "consent", bot: "We'll use your company &amp; requirement details <b>only</b> to match you with relevant seafarers and notify them. You can withdraw consent and request deletion anytime (see Privacy Policy). Do you consent?", type: "quick", options: ["Yes", "No"] },
  { key: "company_name", bot: "What is your company name?", type: "text", placeholder: "Company name" },
  { key: "email", bot: "Company email for candidate notifications?", type: "text", placeholder: "crewing@company.com" },
  { key: "availability", bot: "Required joining timeline?", type: "quick", options: ["Immediate", "Custom date"] },
  { key: "availability_priority", bot: "Is this readiness / availability Mandatory or Preferred?", type: "quick", options: ["Mandatory", "Preferred"] },
  { key: "review", bot: "Here is your AI-generated requirement summary. Review, edit and submit.", type: "review" },
];

export const ChatFlow = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [role, setRole] = useState(null);
  const [flow, setFlow] = useState([]);
  const [idx, setIdx] = useState(0);
  const [messages, setMessages] = useState([]);
  const [form, setForm] = useState({});
  const [typing, setTyping] = useState(false);
  const [input, setInput] = useState("");
  const [multi, setMulti] = useState([]);
  const [summary, setSummary] = useState("");
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [done, setDone] = useState(null);
  const scrollRef = useRef(null);
  const cvInputRef = useRef(null);
  const [cvUploading, setCvUploading] = useState(false);

  useEffect(() => {
    api.get("/config").then((r) => setCfg(r.data));
  }, []);

  useEffect(() => {
    setMessages([
      { id: uid(), from: "bot", text: "⚓ Welcome to CrewingConnect! I'll help you get matched in under 2 minutes. Tap a box below to begin:" },
    ]);
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, typing, done]);

  const addBot = (text) => setMessages((m) => [...m, { id: uid(), from: "bot", text }]);
  const addUser = (text) => setMessages((m) => [...m, { id: uid(), from: "user", text }]);

  const pushBotStep = (step) => {
    setTyping(true);
    setTimeout(() => {
      setTyping(false);
      addBot(step.bot);
    }, 500);
  };

  const startRole = (r) => {
    if (!cfg) return;
    addUser(r === "seeker" ? "I'm looking for a job" : "I'm hiring crew");
    setRole(r);
    const steps = r === "seeker" ? buildSeekerSteps(cfg) : buildProviderSteps(cfg);
    setFlow(steps);
    setIdx(0);
    pushBotStep(steps[0]);
  };

  const advance = (curFlow, curIdx) => {
    const next = curIdx + 1;
    if (next >= curFlow.length) return;
    setIdx(next);
    pushBotStep(curFlow[next]);
  };

  // Let users fix a mistaken answer while filling — steps back one question.
  // Safe before OTP; after verification we never re-enter the mobile/otp steps.
  const goBack = () => {
    if (idx <= 0 || done || !step || step.type === "otp") return;
    const otpIdx = flow.findIndex((f) => f.type === "otp");
    const pastAuth = otpIdx >= 0 && idx > otpIdx;
    const prev = flow[idx - 1];
    if (pastAuth && prev && (prev.type === "otp" || prev.type === "mobile")) return;
    setTyping(false);
    setMessages((m) => {
      const copy = [...m];
      if (copy.length && copy[copy.length - 1].from === "bot") copy.pop();   // current prompt
      if (copy.length && copy[copy.length - 1].from === "user") copy.pop();  // last answer
      return copy;
    });
    setInput("");
    setMulti([]);
    setIdx(idx - 1);
  };

  const genSummary = async (data) => {
    setLoadingSummary(true);
    try {
      const res = await api.post("/ai/summary", { role, data });
      setSummary(res.data.summary || localSummary(role, data));
    } catch {
      setSummary(localSummary(role, data));
    } finally {
      setLoadingSummary(false);
    }
  };

  const step = flow[idx];

  const requestOtp = async (curFlow, curIdx, mobileVal) => {
    try {
      const res = await api.post("/auth/request-otp", { mobile: mobileVal });
      advance(curFlow, curIdx);
      setTimeout(() => addBot(res.data.demo_otp
        ? `(Demo mode) Your OTP is *${res.data.demo_otp}*`
        : `📲 ${res.data.message || "OTP sent"}. Please type the 6-digit code here.`), 700);
    } catch (e) {
      addBot("⚠️ " + (e.response?.data?.detail || "Failed to send OTP."));
    }
  };

  const handleCvUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCvUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/documents?category=${encodeURIComponent("CV / Resume")}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      addUser("📎 " + file.name);
      toast.success("CV saved to your profile");
      advance(flow, idx);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setCvUploading(false);
      if (cvInputRef.current) cvInputRef.current.value = "";
    }
  };

  const [editReturn, setEditReturn] = useState(null);

  const CHIP_KEYS = ["rank", "vessel_type", "sea_time", "sea_time_priority", "coc", "coc_priority", "us_visa", "us_visa_priority", "availability", "availability_priority", "name", "vessel_name", "port", "other_visa"];
  const chipLabel = (k) => ({ rank: "Rank", vessel_type: "Vessel type", sea_time: "Sea time", sea_time_priority: "Sea-time priority", coc: "COC", coc_priority: "COC priority", us_visa: "US visa", us_visa_priority: "US-visa priority", availability: "Availability", availability_priority: "Availability priority", name: "Name", company_name: "Company", vessel_name: "Vessel name", port: "Port", other_visa: "Other visa", rpsl_number: "RPSL number" }[k] || k);
  const editField = (key) => {
    const j = flow.findIndex((f) => f.key === key);
    if (j < 0) return;
    setEditReturn(idx);
    setIdx(j);
    pushBotStep({ ...flow[j], bot: `Update your ${chipLabel(key)}:` });
  };

  const roleBoxes = (
    <div className="grid grid-cols-2 gap-3 animate-fade-up" data-testid="role-select">
      <button
        onClick={() => startRole("seeker")}
        disabled={!cfg}
        className="role-blink group p-4 rounded-xl border-2 border-[#FF5A00] bg-black/20 hover:bg-[#FF5A00]/10 hover:-translate-y-1 transition-transform text-left disabled:opacity-50"
        data-testid="role-seeker-btn"
      >
        <Ship className="w-6 h-6 text-[#FF5A00] mb-2" />
        <div className="font-semibold text-sm">I'm a Seafarer</div>
        <div className="text-xs text-[#8A8F9E]">Looking for a shipping job</div>
      </button>
      <button
        onClick={() => startRole("provider")}
        disabled={!cfg}
        className="role-blink-blue group p-4 rounded-xl border-2 border-[#0066FF] bg-black/20 hover:bg-[#0066FF]/10 hover:-translate-y-1 transition-transform text-left disabled:opacity-50"
        data-testid="role-provider-btn"
      >
        <Building2 className="w-6 h-6 text-[#0066FF] mb-2" />
        <div className="font-semibold text-sm">I'm Hiring Crew</div>
        <div className="text-xs text-[#8A8F9E]">Company / job provider</div>
      </button>
    </div>
  );

  const handleValue = async (value, label) => {
    const s = step;
    // In-flow correction: when editing a single field from the review recap, set it and jump back.
    if (editReturn !== null && !(s.key === "coc" && value === "Others") && !(s.key === "availability" && value === "Custom date")) {
      addUser(label ?? value);
      let ek = s.key;
      if (ek === "coc_other") ek = "coc";
      if (ek === "availability_date") ek = "availability";
      const nf = { ...form, [ek]: value };
      setForm(nf);
      const ret = editReturn;
      setEditReturn(null);
      setIdx(ret);
      if (flow[ret]?.type === "review") genSummary(nf);
      addBot("✅ Updated.");
      return;
    }
    addUser(label ?? value);

    // conditional: COC Others (seeker) -> ask free text
    if (s.key === "coc" && value === "Others") {
      const newForm = { ...form, coc: "" };
      setForm(newForm);
      const newFlow = [...flow];
      newFlow.splice(idx + 1, 0, { key: "coc_other", bot: "Please specify the issuing country/authority.", type: "text", placeholder: "Country / authority" });
      setFlow(newFlow);
      advance(newFlow, idx);
      return;
    }
    // conditional: custom availability date
    if (s.key === "availability" && value === "Custom date") {
      const newFlow = flow.filter((f) => f.key !== "availability_date");
      const ai = newFlow.findIndex((f) => f.key === "availability");
      newFlow.splice(ai + 1, 0, { key: "availability_date", bot: "Enter the date (DD/MM/YYYY).", type: "text", placeholder: "DD/MM/YYYY", format: "date" });
      setFlow(newFlow);
      setForm((f) => ({ ...f, availability: value, availability_date: "" }));
      setIdx(ai + 1);
      pushBotStep(newFlow[ai + 1]);
      return;
    }
    // availability changed to a non-custom value -> drop any stale date step
    if (s.key === "availability") {
      const cleaned = flow.filter((f) => f.key !== "availability_date");
      const ai = cleaned.findIndex((f) => f.key === "availability");
      const nf = { ...form, availability: value };
      delete nf.availability_date;
      setForm(nf);
      setFlow(cleaned);
      const next = cleaned[ai + 1];
      setIdx(ai + 1);
      pushBotStep(next);
      if (next && next.type === "review") genSummary(nf);
      return;
    }

    // map special keys
    let key = s.key;
    let val = value;
    if (s.key === "coc_other") key = "coc";
    if (s.key === "availability_date") key = "availability";

    const newForm = { ...form, [key]: val };
    setForm(newForm);

    // consent No -> reconsider
    if (s.key === "consent" && value === "No") {
      addBot("No problem. Sharing your details helps us match you faster. Tap 'Yes' when you're ready, or you can close this chat.");
      return;
    }

    // mobile -> send OTP (unless a field like RPSL comes before the OTP step)
    if (s.key === "mobile") {
      const digits = value.replace(/\D/g, "");
      if (digits.length < 10 || digits.length > 15) {
        addBot("⚠️ Please enter a valid mobile number (10–15 digits with country code).");
        return;
      }
      if (flow[idx + 1]?.type === "otp") {
        await requestOtp(flow, idx, value);
      } else {
        advance(flow, idx);
      }
      return;
    }

    // RPSL-valid company gate (asked right before the RPSL number step)
    if (s.key === "is_rpsl") {
      const yes = value === "Yes";
      const nf = { ...form, is_rpsl: yes };
      setForm(nf);
      if (yes) {
        advance(flow, idx);   // -> RPSL number step
      } else {
        const cleaned = flow.filter((f) => f.key !== "rpsl_number");
        setFlow(cleaned);
        addBot("Noted — your company will be listed as a Non-RPSL company.");
        await requestOtp(cleaned, idx, nf.mobile);
      }
      return;
    }

    // RPSL number captured -> verify against DG Shipping list, auto-fill company name
    if (s.key === "rpsl_number") {
      let res;
      try {
        res = await api.get("/rpsl/verify", { params: { number: value } });
      } catch {
        addBot("⚠️ Couldn't verify the RPSL number right now. Please try again.");
        return;
      }
      if (!res.data?.found) {
        addBot("⚠️ This RPSL number isn't in the DG Shipping approved registry. Please enter a valid RPSL number to proceed.");
        return;
      }
      const company = res.data.company_name;
      const nf = { ...form, rpsl_number: res.data.license || value, company_name: company };
      setForm(nf);
      setFlow((prev) => prev.filter((f) => f.key !== "company_name"));
      addBot(`✅ RPSL verified — ${company}`);
      if (flow[idx + 1]?.type === "otp") {
        await requestOtp(flow, idx, nf.mobile);
      } else {
        advance(flow, idx);
      }
      return;
    }

    // OTP -> verify + login
    if (s.key === "otp") {
      try {
        const res = await api.post("/auth/verify-otp", { mobile: form.mobile, otp: value, role, ref: getRef() });
        login(res.data.token, res.data.user);
        toast.success("Mobile verified");
        advance(flow, idx);
      } catch (e) {
        addBot("⚠️ " + (e.response?.data?.detail || "Invalid OTP, try again."));
      }
      return;
    }

    // email validation
    if (s.key === "email" && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)) {
      addBot("⚠️ Please enter a valid email address.");
      return;
    }

    // reaching review -> generate summary
    const next = flow[idx + 1];
    if (next && next.type === "review") {
      advance(flow, idx);
      genSummary(newForm);
      return;
    }

    advance(flow, idx);
  };

  const submitProfile = async () => {
    const payload = { ...form, summary, consent: true };
    if (role === "provider") payload.coc = multi.length ? multi : (Array.isArray(form.coc) ? form.coc : [form.coc]);
    try {
      const endpoint = role === "seeker" ? "/seeker/profile" : "/provider/requirement";
      const res = await api.post(endpoint, payload);
      const link = `${window.location.origin}/?ref=${res.data.referral_code}`;
      setDone({ link, referral: res.data.referral_code });
      addUser("Submit ✓");
      setTimeout(() => {
        addBot(role === "seeker"
          ? "✅ Your details are now live. If a company is looking for your kind of experience, they will be able to find and view your profile."
          : "✅ Your requirements have been submitted successfully and will be shared with relevant seafarers!");
      }, 400);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed");
    }
  };

  const renderInput = () => {
    if (done) {
      return (
        <div className="space-y-3" data-testid="chat-done-panel">
          {role === "seeker" && (
            <GetNoticedFaster referralCode={done.referral} onUnlocked={() => navigate("/dashboard")} />
          )}
          <Button
            className="w-full bg-[#FF5A00] hover:bg-[#E04F00] rounded-full gap-2"
            onClick={() => navigate("/dashboard")}
            data-testid="goto-dashboard-btn"
          >
            <LayoutDashboard className="w-4 h-4" /> Go to my Dashboard
          </Button>
        </div>
      );
    }

    if (!role) return null;

    if (!step) return null;

    if (step.type === "upload") {
      return (
        <div className="space-y-2" data-testid="chat-upload">
          <input type="file" ref={cvInputRef} className="hidden" accept=".pdf,.png,.jpg,.jpeg,.txt,.docx" onChange={handleCvUpload} data-testid="chat-cv-input" />
          <div className="flex gap-2">
            <Button
              className="flex-1 bg-[#FF5A00] hover:bg-[#E04F00] rounded-full gap-2"
              disabled={cvUploading}
              onClick={() => cvInputRef.current?.click()}
              data-testid="chat-cv-upload-btn"
            >
              <Upload className="w-4 h-4" /> {cvUploading ? "Uploading..." : "Upload CV / biodata"}
            </Button>
            <Button
              variant="outline"
              className="rounded-full border-[#0066FF] bg-[#0066FF]/10 text-[#4d94ff] hover:bg-[#0066FF] hover:text-white"
              disabled={cvUploading}
              onClick={() => { addUser("Skip for now"); advance(flow, idx); }}
              data-testid="chat-cv-skip-btn"
            >
              Skip for now
            </Button>
          </div>
        </div>
      );
    }

    if (step.type === "quick") {
      return (
        <div className="flex flex-wrap gap-2" data-testid="quick-replies">
          {step.options.map((o) => (
            <button
              key={o}
              onClick={() => handleValue(o)}
              className="px-4 py-2 rounded-full bg-[#0066FF] text-white text-sm font-medium border border-[#0066FF] hover:bg-[#0052cc] shadow-md transition-colors"
              data-testid={`quick-${o}`}
            >
              {o}
            </button>
          ))}
        </div>
      );
    }

    if (step.type === "dropdown") {
      return (
        <div className="flex gap-2">
          <Select onValueChange={(v) => handleValue(v)}>
            <SelectTrigger className="bg-black/30 border-white/10 focus:ring-2 focus:ring-[#FF5A00]" data-testid="chat-dropdown">
              <SelectValue placeholder="Select an option" />
            </SelectTrigger>
            <SelectContent className="bg-[#12131A] border-white/10 text-[#F3F4F6] max-h-64">
              {step.options.map((o) => (
                <SelectItem key={o} value={o} data-testid={`opt-${o}`}>{o}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    }

    if (step.type === "multiselect") {
      return (
        <div className="space-y-3" data-testid="chat-multiselect">
          <div className="grid grid-cols-2 gap-2 max-h-44 overflow-y-auto p-1">
            {step.options.map((o) => (
              <label key={o} className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox
                  checked={multi.includes(o)}
                  onCheckedChange={(c) => setMulti((m) => (c ? [...m, o] : m.filter((x) => x !== o)))}
                  className="border-white/30 data-[state=checked]:bg-[#FF5A00] data-[state=checked]:border-[#FF5A00]"
                  data-testid={`multi-${o}`}
                />
                {o}
              </label>
            ))}
          </div>
          <Button
            size="sm"
            className="bg-[#FF5A00] hover:bg-[#E04F00] rounded-full"
            disabled={!multi.length}
            onClick={() => handleValue(multi, `Any of the following ${multi.length} countr${multi.length === 1 ? "y" : "ies"}: ${multi.join(", ")}`)}
            data-testid="multiselect-confirm"
          >
            Confirm ({multi.length})
          </Button>
        </div>
      );
    }

    if (step.type === "review") {
      return (
        <div className="space-y-3" data-testid="chat-review">
          <div className="flex items-center gap-2 text-[#DFFF00] text-xs font-semibold">
            <Sparkles className="w-4 h-4" /> AI-generated summary
          </div>
          <Textarea
            className="bg-black/30 border-white/10 min-h-[110px] focus:ring-2 focus:ring-[#FF5A00]"
            value={loadingSummary ? "Generating your profile summary..." : summary}
            onChange={(e) => setSummary(e.target.value)}
            disabled={loadingSummary}
            data-testid="summary-textarea"
          />
          <div className="flex flex-wrap gap-2" data-testid="review-edit-chips">
            <span className="w-full text-[11px] text-[#8A8F9E]">Tap any answer to correct it (Mobile &amp; Email are locked after OTP):</span>
            {flow.filter((f) => CHIP_KEYS.includes(f.key) && (form[f.key] ?? "") !== "").map((f) => (
              <button key={f.key} onClick={() => editField(f.key)} className="text-xs bg-[#0066FF]/15 border border-[#0066FF]/40 text-[#4d94ff] rounded-full px-3 py-1 hover:bg-[#0066FF] hover:text-white transition-colors" data-testid={`edit-chip-${f.key}`}>
                {chipLabel(f.key)}: {Array.isArray(form[f.key]) ? form[f.key].join(", ") : form[f.key]} ✎
              </button>
            ))}
          </div>
          <Button
            className="w-full bg-[#FF5A00] hover:bg-[#E04F00] rounded-full gap-2"
            disabled={loadingSummary || !summary}
            onClick={submitProfile}
            data-testid="submit-profile-btn"
          >
            <CheckCircle2 className="w-4 h-4" /> Submit
          </Button>
        </div>
      );
    }

    // text / mobile / otp
    const isOtp = step.type === "otp";
    const isMobile = step.type === "mobile";
    if (isMobile) {
      return (
        <div className="flex gap-2" data-testid="chat-mobile">
          <div className="flex-1">
            <PhoneField
              value={input}
              onChange={setInput}
              onEnter={() => { if (input) { handleValue(input); setInput(""); } }}
              testId="chat-phone"
            />
          </div>
          <Button
            className="bg-[#FF5A00] hover:bg-[#E04F00] shrink-0"
            disabled={!input || !isValidPhoneNumber(input)}
            onClick={() => { handleValue(input); setInput(""); }}
            data-testid="chat-send-btn"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      );
    }
    return (
      <div className="flex gap-2">
        <Input
          className="bg-black/30 border-white/10 focus:ring-2 focus:ring-[#FF5A00] font-data"
          placeholder={step.placeholder || "Type your answer"}
          value={input}
          maxLength={isOtp ? 6 : (step.format === "date" ? 10 : undefined)}
          onChange={(e) => {
            const v = e.target.value;
            if (isOtp) setInput(v.replace(/\D/g, ""));
            else if (isMobile) setInput(v.replace(/[^\d+]/g, ""));
            else if (step.format === "date") setInput(formatDateInput(v));
            else setInput(v);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (input || step.optional)) {
              handleValue(input);
              setInput("");
            }
          }}
          data-testid="chat-text-input"
        />
        {step.optional && (
          <Button variant="ghost" className="text-[#8A8F9E] hover:text-white" onClick={() => { handleValue("", "Skip"); setInput(""); }} data-testid="chat-skip-btn">
            Skip
          </Button>
        )}
        <Button
          className="bg-[#FF5A00] hover:bg-[#E04F00] shrink-0"
          disabled={!input && !step.optional}
          onClick={() => { handleValue(input); setInput(""); }}
          data-testid="chat-send-btn"
        >
          <Send className="w-4 h-4" />
        </Button>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full rounded-2xl overflow-hidden border border-white/15 bg-transparent">
      {/* chat header */}
      <div className="flex items-center gap-3 px-4 py-3 backdrop-blur-md bg-black/25 border-b border-white/10 z-10">
        <div className="w-10 h-10 rounded-full bg-[#25D366] flex items-center justify-center">
          <Anchor className="w-5 h-5 text-black" strokeWidth={2.5} />
        </div>
        <div className="leading-tight">
          <div className="font-semibold text-sm">CrewingConnect Bot</div>
          <div className="text-[11px] text-[#25D366] flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-[#25D366]" /> online
          </div>
        </div>
      </div>

      {/* messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
        {messages.map((m) => (
          <div key={m.id} className={`flex ${m.from === "user" ? "justify-end" : "justify-start"} animate-fade-up`}>
            <div
              className={
                m.from === "user"
                  ? "bg-[#FF5A00] text-white rounded-2xl rounded-tr-sm px-4 py-2.5 text-sm max-w-[80%] shadow"
                  : "bg-[#12131A] text-[#F3F4F6] border border-white/10 rounded-2xl rounded-tl-sm px-4 py-2.5 text-sm max-w-[85%] shadow"
              }
              dangerouslySetInnerHTML={{ __html: m.text.replace(/\*(.*?)\*/g, "<b>$1</b>") }}
            />
          </div>
        ))}
        {typing && (
          <div className="flex justify-start">
            <div className="bg-[#12131A] border border-white/10 rounded-2xl rounded-tl-sm px-4 py-3 flex gap-1">
              <span className="w-2 h-2 rounded-full bg-[#8A8F9E] typing-dot" />
              <span className="w-2 h-2 rounded-full bg-[#8A8F9E] typing-dot" style={{ animationDelay: "0.2s" }} />
              <span className="w-2 h-2 rounded-full bg-[#8A8F9E] typing-dot" style={{ animationDelay: "0.4s" }} />
            </div>
          </div>
        )}
        {!role && !done && <div className="pt-1">{roleBoxes}</div>}
        {role && cfg && (
          <div className="pt-2 animate-fade-up" data-testid="chat-input-inline">
            {(() => {
              const otpIdx = flow.findIndex((f) => f.type === "otp");
              const pastAuth = otpIdx >= 0 && idx > otpIdx;
              const prev = flow[idx - 1];
              const show = !done && step && idx > 0 && !typing && step.type !== "otp"
                && (pastAuth ? (prev && prev.type !== "otp" && prev.type !== "mobile") : true);
              return show ? (
                <button
                  onClick={goBack}
                  className="mb-2 inline-flex items-center gap-1.5 text-xs text-[#8A8F9E] hover:text-white transition-colors"
                  data-testid="chat-back-btn"
                >
                  <Undo2 className="w-3.5 h-3.5" /> Correct last answer
                </button>
              ) : null;
            })()}
            {renderInput()}
          </div>
        )}
      </div>
    </div>
  );
};
